import { Component, OnInit, ViewChild } from '@angular/core'
import { MarketODataService, IMarket, ProviderODataService } from '@exchange/domain/.'
import { ActivatedRoute, Router } from '@angular/router'
import { ToastrService } from 'ngx-toastr'
import { take } from 'rxjs/operators'
import { UtilService } from '@exchange/services/.'
import {
  ActionButtonsColumnRendererComponent,
  BooleanFilterComponent,
  ConfirmModalComponent,
  GridWrapperComponent,
  Option
} from 'common-components'
import { BsModalService } from 'ngx-bootstrap/modal'

@Component({
  selector: 'app-market-list',
  templateUrl: './market-list.component.html',
  standalone: false
})
export class MarketListComponent implements OnInit {
  busy: any = null
  model: IMarket = null
  states: Option[] = []

  @ViewChild('grid', { static: true }) grid: GridWrapperComponent

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private odata: MarketODataService,
    private providerService: ProviderODataService,
    public util: UtilService,
    private toastr: ToastrService,
    private modalService: BsModalService
  ) {}

  ngOnInit() {
    this.initializeGrid()
    const sm = this.route.snapshot.queryParams['successMessage']
    if (sm) {
      this.toastr.success(sm)
    }
  }

  addEntity(): void {
    this.router.navigate(['/system', 'markets', 0])
  }

  editClicked(data) {
    const model = data as IMarket
    this.router.navigate(['/system', 'markets', model.Id])
  }

  deleteClicked(data) {
    this.model = data as IMarket
    this.modalService
      .show(ConfirmModalComponent, {
        initialState: {
          title: `Delete Market?`,
          message: `Are you sure you want to delete the selected Market?`
        }
      })
      .content.onClose.subscribe(userConfirm => userConfirm && this.deleteModel())
  }

  deleteModel(): void {
    const errors = []

    this.providerService
      .query()
      .filter(`MarketId eq ${this.model.Id}`)
      .exec()
      .pipe(take(1))
      .subscribe(providers => {
        if (providers.rowCount > 0) {
          errors.push('Market cannot be deleted until all providers are removed.')
        }

        this.toastr.clear()
        if (errors.length > 0) {
          this.toastr.error(errors.join('<br />'))
          return
        }
        this.odata
          .delete(this.model.Id)
          .exec()
          .subscribe(response => this.grid.getData())

        this.showError.bind(this)
      })
  }

  initializeGrid() {
    this.grid.defaultExpand = 'OrganizationPhoneNumber1'
    this.grid.frameworkComponents = {
      boolFilter: BooleanFilterComponent
    }
    this.grid.gridOptions.context = {
      canEdit: true,
      canDelete: true,
      editHandler: this.editClicked.bind(this),
      deleteHandler: this.deleteClicked.bind(this)
    }
    this.grid.gridOptions.columnDefs = [
      {
        headerName: '',
        field: 'Actions',
        pinned: 'left',
        width: 100,
        cellRenderer: ActionButtonsColumnRendererComponent
      },
      {
        headerName: 'CHI Market ID',
        field: 'Id',
        width: 75,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Name',
        field: 'Name',
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Is Active',
        field: 'IsActive',
        width: 60,
        cellRenderer: GridWrapperComponent.BooleanRenderer(),
        filter: 'boolFilter'
      },
      {
        headerName: 'Program Name',
        field: 'OrganizationName',
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Program Phone Number',
        field: 'OrganizationPhoneNumber1.Number',
        width: 100,
        menuTabs: [],
        cellRenderer: GridWrapperComponent.PhoneNumberRenderer()
      },
      {
        headerName: 'Screening Teams Channel Configured',
        field: 'ScreeningTeamsChannelWebhook',
        width: 100,
        cellRenderer: GridWrapperComponent.BooleanRenderer()
      },
      {
        headerName: 'Admission Teams Channel Configured',
        field: 'AdmissionTeamsChannelWebhook',
        width: 100,
        cellRenderer: GridWrapperComponent.BooleanRenderer()
      }
    ]
    this.grid.odata = this.odata
  }

  showError(response): void {
    this.busy = null

    const msg = this.util.getErrorString(response)

    this.toastr.error(msg)
  }
}
